import { queryKeys } from '@/lib/query-keys';
import { apiClient } from '@borradh-workspace/api-client';
import { subscriptionResponseSchema } from '@borradh-workspace/contracts';
import { queryOptions, useQuery } from '@tanstack/react-query';
import type { SubscriptionResponse } from '../types';

/** How often a tab with a billing issue re-checks, so the banner clears after payment. */
export const BILLING_ISSUE_POLL_MS = 30_000;

export const getSubscriptionQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.billing.subscription(),
    queryFn: () =>
      apiClient.get<SubscriptionResponse>('billing/subscription', {
        schema: subscriptionResponseSchema,
      }),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
    // Poll ONLY while there is a billing issue, so the recovery banner clears
    // soon after the customer pays. This query sits under every signed-in
    // page (BillingRecoveryFrame), and an unconditional 30s poll made it half
    // of all production API requests — ~44k/day, nearly all from healthy
    // accounts with nothing to show. A NEW problem does not need polling: the
    // first request the API refuses for billing invalidates this query
    // (registerBillingRecoveryHandler in main.tsx), and mount/focus refetch.
    refetchInterval: (query) =>
      query.state.data?.billingIssue ? BILLING_ISSUE_POLL_MS : false,
  });

export const useGetSubscription = ({
  enabled = true,
}: { enabled?: boolean } = {}) => {
  const query = useQuery({ ...getSubscriptionQueryOptions(), enabled });

  return {
    subscription: query.data?.subscription ?? null,
    billingIssue: query.data?.billingIssue ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
};
